import React, { useCallback, useRef } from 'react'
import { CloudUploadOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Tooltip } from '@surecompanies/core_components'
import type { DragEvent } from 'react'
import styles from './PlanOverridesComponents.module.scss'

const ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
]

// Include file extensions alongside MIME types for reliable cross-OS browser filtering
const ACCEPT_ATTR = [
  ...ACCEPTED_TYPES,
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.pdf',
  '.doc',
  '.docx',
  '.xlsx',
  '.csv',
].join(',')

interface Props {
  onFilesSelected: (files: File[]) => void
  disabled?: boolean
  fileCount?: number
  maxFiles?: number
}

export default function DocumentDropzone({
  onFilesSelected,
  disabled,
  fileCount,
  maxFiles,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleClick = useCallback(() => {
    if (!disabled) inputRef.current?.click()
  }, [disabled])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') handleClick()
    },
    [handleClick]
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      if (files.length > 0) onFilesSelected(files)
      // reset so the same file can be re-selected after removal
      e.target.value = ''
    },
    [onFilesSelected]
  )

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      if (disabled) return
      const files = Array.from(e.dataTransfer.files)
      if (files.length > 0) onFilesSelected(files)
    },
    [disabled, onFilesSelected]
  )

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }, [])

  return (
    <div>
      <div
        className={`${styles.documentDropzone} ${disabled ? styles.documentDropzoneDisabled : ''}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Upload evidence documents"
        aria-disabled={disabled}
      >
        <span className={styles.documentDropzoneIconBox}>
          <CloudUploadOutlined className={styles.documentDropzoneIcon} />
        </span>
        <div className={styles.documentDropzoneText}>
          <p className={styles.documentDropzonePrimary}>
            <span className={styles.documentDropzoneLink}>Click to upload</span>{' '}
            or drag and drop
          </p>
          <p className={styles.documentDropzoneHint}>
            Supported: PDF, CSV, XLSX, DOCX, images{' '}
            <Tooltip title="Image ≤ 5 MB · PDF ≤ 15 MB · doc/CSV ≤ 10 MB">
              <InfoCircleOutlined className={styles.documentDropzoneHintIcon} />
            </Tooltip>
          </p>
          {fileCount !== undefined && maxFiles !== undefined && (
            <p className={styles.documentDropzoneSlotCount}>
              {fileCount} of {maxFiles} uploaded
            </p>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          multiple
          onChange={handleChange}
          className={styles.inputHidden}
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>
    </div>
  )
}
